// src/components/settings/PipelineSettings.js
// SEAM 4 VERIFIER: qpipe-4d1a7c35-settings
//
// Settings → Pipeline. The org's quote pipeline stages.
//
// Stages live in organizations/{orgId}/settings/pipeline — NOT on the org doc,
// which the customer portal downloads whole. firestore.rules lets staff READ
// that document and denies every client WRITE, so this screen saves through
// the savePipelineStages Cloud Function, which enforces the same rules shown
// here (system stages can't be renamed, moved past each other, or deleted; a
// stage's type is fixed once saved; at least one Lost stage).
//
// Until an org saves (or its first estimate is placed) the document doesn't
// exist and the starter set is shown; custom rows carry tmpl_ placeholder ids
// the server translates on save.
//
// DRAFT MODEL (qpipe-4d1a7c35-settings-draft, UI test 5 follow-up):
//   - `base` is the saved list + revision the edits started from; `rows` is
//     the draft. Unsaved changes are DERIVED (draft vs base), listed on screen,
//     and nothing looks final until Save: a deleted row stays visible, struck
//     through, with Undo.
//   - Leaving with unsaved changes asks first (browser refresh/close, sidebar
//     links, other Settings tabs). The browser Back button can't be intercepted
//     with this app's router.
//   - Occupant counts refresh whenever this tab opens and whenever the browser
//     tab regains focus, and are re-counted FRESH at Save before the delete
//     confirmation / over-cap warning is decided — never from a cached zero.
//   - Save sends the BASE revision. If the stages were saved elsewhere
//     meanwhile, the server refuses; the screen says so, lists exactly which of
//     your changes were not saved, and only discards them when you choose to
//     load the latest version — then lists them again as discarded.
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { doc, getDoc, onSnapshot, collection, query, where, getCountFromServer } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ListOrdered, Lock, ArrowUp, ArrowDown, Trash2, Plus, CheckCircle, AlertTriangle, EyeOff, Undo2, RefreshCw } from 'lucide-react';
import {
    resolveDocTerminology,
    resolvePipelineStages,
    isClosedStageType,
    PIPELINE_MAX_STAGES,
    PIPELINE_LABEL_MAX,
    PIPELINE_STALE_DAYS_MAX,
    PIPELINE_TEMPLATE_ID_PREFIX,
} from '../../utils/quoteSchema';
import {
    PIPELINE_DELETE_OCCUPANT_CAP,
    draftRowsFrom,
    newDraftRow,
    pendingChanges,
    hasPendingChanges,
    summarizePendingChanges,
    removedStageIds,
    saveInputFrom,
    occupancyDecision,
    isStaleListError,
} from '../../utils/pipelineStageDraft';

const TYPE_LABELS = { open: 'Open', won: 'Won', lost: 'Lost', deferred: 'Deferred' };
const TYPE_CHIP = {
    open:     'bg-blue-50 text-blue-700 border-blue-200',
    won:      'bg-green-50 text-green-700 border-green-200',
    lost:     'bg-red-50 text-red-700 border-red-200',
    deferred: 'bg-amber-50 text-amber-700 border-amber-200',
};

const LEAVE_WARNING = 'You have unsaved changes to your pipeline stages. Leave without saving?';

export default function PipelineSettings({ currentUserProfile }) {
    const orgId = currentUserProfile?.organizationId;
    const containerRef = useRef(null);

    const [serverStages, setServerStages]     = useState(null);
    const [serverRevision, setServerRevision] = useState(0);
    const [base, setBase]                     = useState(null); // { stages, revision } the draft started from
    const [rows, setRows]                     = useState([]);
    const [adoptAtRevision, setAdoptAtRevision] = useState(null); // set after a successful save
    const [counts, setCounts]                 = useState({});
    const [countsLoading, setCountsLoading]   = useState(false);
    const [docWord, setDocWord]               = useState('Quote');
    const [newLabel, setNewLabel]             = useState('');
    const [newType, setNewType]               = useState('open');
    const [confirmMoves, setConfirmMoves]     = useState(null); // occupancyDecision
    const [overCap, setOverCap]               = useState(null);  // occupancyDecision
    const [staleNotice, setStaleNotice]       = useState(null);  // { phase: 'refused' | 'discarded', lines }
    const [checking, setChecking]             = useState(false);
    const [isSaving, setIsSaving]             = useState(false);
    const [saved, setSaved]                   = useState(false);
    const [error, setError]                   = useState('');
    const [loading, setLoading]               = useState(true);

    // ── live saved list ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId) return undefined;
        // Aggregate/org-default surface — org term, NOT resolveDocTerminology(quote.docType).
        getDoc(doc(db, 'organizations', orgId))
            .then((snap) => { if (snap.exists()) setDocWord(resolveDocTerminology(snap.data().quoteTerminologyDefault).word); })
            .catch(() => {});
        return onSnapshot(doc(db, `organizations/${orgId}/settings`, 'pipeline'), (snap) => {
            const data = snap.exists() ? snap.data() : null;
            setServerRevision(Number.isInteger(data?.revision) ? data.revision : 0);
            setServerStages(resolvePipelineStages(data));
            setLoading(false);
        }, (err) => {
            console.error('PipelineSettings: could not load stages', err);
            setError('Could not load pipeline stages.');
            setLoading(false);
        });
    }, [orgId]);

    const changes = useMemo(() => pendingChanges(base?.stages, rows), [base, rows]);
    const dirty = hasPendingChanges(changes);
    const changeLines = useMemo(() => summarizePendingChanges(changes), [changes]);
    // Saved elsewhere since this draft started: a save now would be refused.
    const conflict = dirty && !!base && adoptAtRevision === null && serverRevision !== base.revision;

    const adopt = useCallback((stages, revision) => {
        setBase({ stages, revision });
        setRows(draftRowsFrom(stages));
    }, []);

    // Adopt the saved list when there's no draft, or once our own save lands.
    useEffect(() => {
        if (!serverStages) return;
        if (adoptAtRevision !== null) {
            if (serverRevision >= adoptAtRevision) {
                adopt(serverStages, serverRevision);
                setAdoptAtRevision(null);
            }
            return;
        }
        if (!base || !dirty) adopt(serverStages, serverRevision);
    }, [serverStages, serverRevision, adoptAtRevision, dirty]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── occupant counts ─────────────────────────────────────────────────────
    // Returns { id: count }. strict: throw instead of recording null, for the
    // Save-time check that must never proceed on an unknown count.
    const countStages = useCallback(async (ids, { strict = false } = {}) => {
        const next = {};
        await Promise.all(ids.filter((id) => id && !id.startsWith(PIPELINE_TEMPLATE_ID_PREFIX)).map(async (id) => {
            try {
                const agg = await getCountFromServer(query(collection(db, `organizations/${orgId}/quotes`), where('pipelineStageId', '==', id)));
                next[id] = agg.data().count;
            } catch (e) {
                if (strict) throw e;
                next[id] = null;
            }
        }));
        setCounts((prev) => ({ ...prev, ...next }));
        return next;
    }, [orgId]);

    const savedIdsKey = (serverStages || []).map((s) => s.id).join('|');
    const refreshAllCounts = useCallback(async () => {
        if (!orgId || !savedIdsKey) return;
        setCountsLoading(true);
        try { await countStages(savedIdsKey.split('|')); } finally { setCountsLoading(false); }
    }, [orgId, savedIdsKey, countStages]);

    // Every time this tab opens (mount) and whenever the saved list changes.
    useEffect(() => { refreshAllCounts(); }, [refreshAllCounts]);

    // …and whenever the user comes back to this browser tab (estimates may
    // have moved in another tab or on another device meanwhile).
    useEffect(() => {
        const onVisible = () => { if (document.visibilityState === 'visible') refreshAllCounts(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [refreshAllCounts]);

    // ── leave guard ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        // Capture phase on document runs before React's own click handling, so
        // cancelling here stops sidebar NavLinks and Settings tab buttons.
        const onClickCapture = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const el = e.target && e.target.closest ? e.target.closest('a[href], [data-settings-tab]') : null;
            if (!el) return;
            if (containerRef.current && containerRef.current.contains(el)) return;
            if (el.getAttribute('data-settings-tab') === 'pipeline') return;
            if (el.tagName === 'A' && (el.getAttribute('target') === '_blank' || el.hasAttribute('download'))) return;
            if (!window.confirm(LEAVE_WARNING)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClickCapture, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClickCapture, true);
        };
    }, [dirty]);

    // ── editing ─────────────────────────────────────────────────────────────
    const word = docWord.toLowerCase();
    const baseById = useMemo(() => new Map((base?.stages || []).map((s) => [s.id, s])), [base]);

    const edit = (fn) => {
        setRows((prev) => fn(prev.map((r) => ({ ...r }))));
        setSaved(false); setError(''); setConfirmMoves(null); setOverCap(null);
        if (staleNotice?.phase === 'discarded') setStaleNotice(null);
    };

    const move = (i, dir) => edit((r) => {
        const j = i + dir;
        if (j < 0 || j >= r.length) return r;
        [r[i], r[j]] = [r[j], r[i]];
        return r;
    });

    const toggleDelete = (i) => edit((r) => {
        if (r[i].isNew) return r.filter((_, k) => k !== i); // never saved — nothing to keep
        r[i].pendingDelete = !r[i].pendingDelete;
        return r;
    });

    const addStage = () => {
        const label = newLabel.trim();
        if (!label) return;
        edit((r) => {
            const row = newDraftRow(label, newType);
            if (isClosedStageType(newType)) return [...r, row];
            const firstClosed = r.findIndex((x) => isClosedStageType(x.type));
            if (firstClosed === -1) return [...r, row];
            r.splice(firstClosed, 0, row);
            return r;
        });
        setNewLabel('');
    };

    const discardChanges = () => {
        setRows(draftRowsFrom(base?.stages || []));
        setError(''); setConfirmMoves(null); setOverCap(null); setStaleNotice(null);
    };

    const loadLatest = () => {
        const lines = changeLines;
        adopt(serverStages, serverRevision);
        setConfirmMoves(null); setOverCap(null); setError('');
        setStaleNotice({ phase: 'discarded', lines });
    };

    // ── saving ──────────────────────────────────────────────────────────────
    const doSave = async () => {
        setConfirmMoves(null);
        setIsSaving(true);
        setError('');
        try {
            const res = await httpsCallable(functions, 'savePipelineStages')({
                revision: base.revision,
                stages: saveInputFrom(rows),
            });
            setAdoptAtRevision(res?.data?.revision ?? base.revision + 1);
            setStaleNotice(null);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            console.error('PipelineSettings: save failed', err);
            if (isStaleListError(err)) {
                // Keep the draft on screen; say exactly what was NOT saved.
                setStaleNotice({ phase: 'refused', lines: changeLines });
            } else {
                setError(err?.message || 'Failed to save. Please try again.');
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = async () => {
        setError(''); setOverCap(null); setConfirmMoves(null);
        const removed = removedStageIds(rows);
        if (removed.length) {
            setChecking(true);
            let fresh;
            try {
                fresh = await countStages(removed, { strict: true });
            } catch (e) {
                console.error('PipelineSettings: occupant count failed', e);
                setError(`Couldn't check how many ${word}s are in the stages you're deleting, so nothing was saved. Please try again.`);
                return;
            } finally {
                setChecking(false);
            }
            const labels = Object.fromEntries((base?.stages || []).map((s) => [s.id, s.label]));
            const decision = occupancyDecision(removed, fresh, labels);
            if (decision.overCap) { setOverCap(decision); return; }
            if (decision.needsConfirm) { setConfirmMoves(decision); return; }
        }
        doSave();
    };

    if (!orgId) return null;
    if (loading || !base) return <div className="p-6 text-sm text-gray-400">Loading…</div>;

    const plural = (n) => `${n} ${n === 1 ? word : `${word}s`}`;
    const busy = isSaving || checking || adoptAtRevision !== null;
    const refused = staleNotice?.phase === 'refused';

    return (
        <div ref={containerRef} className="p-6 space-y-6 max-w-3xl">
            <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <ListOrdered size={18} className="text-gray-500" /> Pipeline
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    The stages a {word} moves through. Stages marked with a lock follow the {word}'s status
                    automatically. Your own stages are set by hand from the {word}. "Stale after" is how long
                    a {word} can sit in a stage before it needs attention.
                </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                <EyeOff size={16} className="shrink-0 mt-0.5 text-gray-400" />
                <p>Internal only. Customers never see stages — not on the {word}, the email, or the customer portal.</p>
            </div>

            {/* Stale list: refused save, or saved elsewhere while editing */}
            {(refused || conflict) && (
                <div className="p-4 bg-red-50 border border-red-300 rounded-xl space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
                        <AlertTriangle size={16} />
                        {refused ? 'Not saved — the stages were changed somewhere else' : 'The stages were changed somewhere else while you were editing'}
                    </p>
                    <p className="text-sm text-red-800">
                        {refused
                            ? 'Someone saved a different version of the stages after you started editing, so your changes were NOT saved. They are still on screen. These are the changes that were not saved:'
                            : 'Someone saved a different version of the stages after you started. Saving now would be refused. Your unsaved changes:'}
                    </p>
                    <ul className="list-disc pl-5 text-sm text-red-800">
                        {(refused ? staleNotice.lines : changeLines).map((l) => <li key={l}>{l}</li>)}
                    </ul>
                    <p className="text-sm text-red-800">To continue, load the latest stages and re-apply any of these you still need.</p>
                    <button type="button" onClick={loadLatest}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                        <RefreshCw size={14} /> Load the latest stages (discards the changes above)
                    </button>
                </div>
            )}

            {staleNotice?.phase === 'discarded' && (
                <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-2">
                    <p className="text-sm font-semibold text-amber-900">Loaded the latest stages. These changes of yours were discarded and NOT saved:</p>
                    <ul className="list-disc pl-5 text-sm text-amber-900">
                        {staleNotice.lines.map((l) => <li key={l}>{l}</li>)}
                    </ul>
                    <p className="text-sm text-amber-900">Re-apply any you still need, then press Save stages.</p>
                    <button type="button" onClick={() => setStaleNotice(null)} className="px-3 py-1.5 text-sm font-medium text-amber-900 bg-white border border-amber-300 rounded-lg">Dismiss</button>
                </div>
            )}

            {/* Unsaved-changes cue */}
            {dirty && !refused && !conflict && (
                <div className="p-3 bg-blue-50 border border-blue-300 rounded-xl text-sm text-blue-900 space-y-1">
                    <p className="font-semibold">Unsaved changes — nothing is saved until you press Save stages.</p>
                    <ul className="list-disc pl-5">
                        {changeLines.map((l) => <li key={l}>{l}</li>)}
                    </ul>
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    <div className="col-span-5">Stage</div>
                    <div className="col-span-2">Type</div>
                    <div className="col-span-2">Stale after</div>
                    <div className="col-span-1 text-right flex items-center justify-end gap-1">
                        {docWord}s
                        <button type="button" onClick={refreshAllCounts} disabled={countsLoading} title="Refresh counts" aria-label="Refresh counts"
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
                            <RefreshCw size={11} className={countsLoading ? 'animate-spin' : ''} />
                        </button>
                    </div>
                    <div className="col-span-2 text-right">Order</div>
                </div>
                {rows.map((r, i) => {
                    const closed = isClosedStageType(r.type);
                    const staleDisabled = closed || r.id === 'draft' || r.pendingDelete;
                    const count = r.isNew ? 0 : counts[r.id];
                    const b = r.isNew ? null : baseById.get(r.id);
                    const edited = !!b && !r.pendingDelete && (r.label.trim() !== b.label
                        || (b.staleAfterDays == null ? '' : String(b.staleAfterDays)) !== r.staleInput);
                    return (
                        <div key={r.key} className={`grid grid-cols-12 gap-2 items-center px-4 py-2 ${r.pendingDelete ? 'bg-red-50' : ''}`}>
                            <div className="col-span-12 sm:col-span-5">
                                <div className="flex items-center gap-2">
                                    {r.isSystem && <Lock size={14} className="text-gray-400 shrink-0" title="Follows status automatically" />}
                                    <input
                                        type="text"
                                        value={r.label}
                                        maxLength={PIPELINE_LABEL_MAX}
                                        disabled={r.isSystem || r.pendingDelete}
                                        onChange={(e) => { const v = e.target.value; edit((rs) => { rs[i].label = v; return rs; }); }}
                                        className={`w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50 disabled:text-gray-600 ${r.pendingDelete ? 'line-through text-red-700' : ''}`}
                                    />
                                    {r.isNew && <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-700 bg-blue-100 rounded">New</span>}
                                    {edited && <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-700 bg-blue-100 rounded">Edited</span>}
                                </div>
                                {r.pendingDelete && (
                                    <p className="mt-1 text-xs text-red-700">
                                        Will be deleted when you press Save stages
                                        {count > 0 ? ` — ${plural(count)} will move back to the stage that matches their status` : ''}.
                                    </p>
                                )}
                            </div>
                            <div className="col-span-4 sm:col-span-2">
                                {r.isNew ? (
                                    <select
                                        value={r.type}
                                        onChange={(e) => { const v = e.target.value; edit((rs) => { rs[i].type = v; if (isClosedStageType(v)) rs[i].staleInput = ''; return rs; }); }}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
                                    >
                                        {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                ) : (
                                    <span className={`inline-block px-2 py-0.5 text-xs border rounded-full ${TYPE_CHIP[r.type]}`}>{TYPE_LABELS[r.type]}</span>
                                )}
                            </div>
                            <div className="col-span-4 sm:col-span-2 flex items-center gap-1">
                                <input
                                    type="number"
                                    min={1}
                                    max={PIPELINE_STALE_DAYS_MAX}
                                    value={staleDisabled ? '' : r.staleInput}
                                    disabled={staleDisabled}
                                    placeholder={closed || r.id === 'draft' ? 'n/a' : '—'}
                                    onChange={(e) => { const v = e.target.value; edit((rs) => { rs[i].staleInput = v; return rs; }); }}
                                    className="w-16 px-2 py-1.5 text-sm border border-gray-200 rounded-lg disabled:bg-gray-50"
                                />
                                {!staleDisabled && <span className="text-xs text-gray-400">days</span>}
                            </div>
                            <div className="col-span-1 text-right text-sm text-gray-500">
                                {r.isNew ? '—' : (count == null ? '—' : count)}
                            </div>
                            <div className="col-span-3 sm:col-span-2 flex items-center justify-end gap-1">
                                <button type="button" onClick={() => move(i, -1)} disabled={r.isSystem || r.pendingDelete || i === 0}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move up"><ArrowUp size={14} /></button>
                                <button type="button" onClick={() => move(i, 1)} disabled={r.isSystem || r.pendingDelete || i === rows.length - 1}
                                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30" aria-label="Move down"><ArrowDown size={14} /></button>
                                {r.pendingDelete ? (
                                    <button type="button" onClick={() => toggleDelete(i)}
                                        className="flex items-center gap-1 px-1.5 py-1 text-xs font-medium text-red-700 rounded hover:bg-red-100" aria-label="Undo delete">
                                        <Undo2 size={13} /> Undo
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => toggleDelete(i)} disabled={r.isSystem}
                                        className="p-1 rounded text-red-500 hover:bg-red-50 disabled:opacity-0" aria-label="Delete stage"><Trash2 size={14} /></button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {rows.filter((r) => !r.pendingDelete).length < PIPELINE_MAX_STAGES && (
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        type="text"
                        value={newLabel}
                        maxLength={PIPELINE_LABEL_MAX}
                        placeholder="New stage name"
                        onChange={(e) => setNewLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
                        className="flex-1 min-w-[12rem] px-3 py-2 text-sm border border-gray-200 rounded-lg"
                    />
                    <select value={newType} onChange={(e) => setNewType(e.target.value)} className="px-2 py-2 text-sm border border-gray-200 rounded-lg">
                        {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button type="button" onClick={addStage} disabled={!newLabel.trim()}
                        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                        <Plus size={14} /> Add stage
                    </button>
                </div>
            )}
            <p className="text-xs text-gray-400 -mt-4">A stage's type can't be changed after it's saved.</p>

            {confirmMoves && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                    <p className="text-sm text-amber-900">
                        {confirmMoves.perStage.map((x) => `${plural(x.count)} in "${x.label}"`).join(', ')}.
                        {' '}When you save, {confirmMoves.total === 1 ? `that ${word} moves` : `those ${word}s move`} back to the stage that matches {confirmMoves.total === 1 ? 'its' : 'their'} status.
                    </p>
                    <div className="flex gap-2">
                        <button type="button" onClick={doSave} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700">Delete and move</button>
                        <button type="button" onClick={() => setConfirmMoves(null)} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg">Cancel</button>
                    </div>
                </div>
            )}

            {overCap && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <p>
                        {overCap.perStage.map((x) => `${plural(x.count)} in "${x.label}"`).join(', ')} — {overCap.total} in total.
                        {' '}A save can move at most {PIPELINE_DELETE_OCCUPANT_CAP}. Move some of them to another stage first. Nothing was saved.
                    </p>
                </div>
            )}

            {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <p>{error}</p>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <button type="button" onClick={handleSave} disabled={!dirty || busy || !!confirmMoves || refused || conflict}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[#5F4EC9] rounded-lg hover:opacity-90 disabled:opacity-50">
                    {checking ? 'Checking…' : isSaving || adoptAtRevision !== null ? 'Saving…' : 'Save stages'}
                </button>
                {dirty && !busy && (
                    <button type="button" onClick={discardChanges}
                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Discard changes</button>
                )}
                {dirty && !busy && (
                    <span className="text-sm font-medium text-blue-700">
                        {changeLines.length} unsaved change{changeLines.length === 1 ? '' : 's'}
                    </span>
                )}
                {saved && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle size={14} /> Saved</span>}
            </div>
        </div>
    );
}
